import { useEffect, useState } from "react";
import {
  fetchExplorerCompanies,
  fetchExplorerBuildings,
  fetchExplorerSuppliers,
  fetchExplorerSupplier,
  fetchExplorerMaterials,
  fetchExplorerMaterial,
  fetchCrossClientSuppliers,
  fetchCrossClientSupplier,
} from "./supplierExplorerApi";

// Internal back-office tool: cross-references fornecedor × cliente × obra × material × preço.
// Reads PROD data (dev_2) via the dedicated back-office API. Admin-only.

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join("/") : "-");
const supplierLabel = (s) => s?.name || `Fornecedor #${s?.supplierId}`;

const th = { textAlign: "left", padding: "6px 10px", borderBottom: "2px solid #e0e0e0", fontWeight: 600 };
const thR = { ...th, textAlign: "right" };
const td = { padding: "6px 10px", borderBottom: "1px solid #eee", verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const tabStyle = (active) => ({
  padding: "6px 12px",
  border: "1px solid #c9c9c9",
  borderRadius: 6,
  background: active ? "#2d6cdf" : "#fff",
  color: active ? "#fff" : "#333",
  cursor: "pointer",
});
const Outlier = () => (
  <span title="Faixa de preço muito ampla (máx/mín > 3) — possível erro de dado" style={{ color: "#c0392b" }}>
    {" "}⚠
  </span>
);

const ContactBlock = ({ contact }) => {
  if (!contact) return null;
  const { phones = [], emails = [], contacts = [], city, state } = contact;
  if (!phones.length && !emails.length && !contacts.length && !city && !state) return null;
  return (
    <div style={{ fontSize: 13, color: "#444", margin: "4px 0 10px", lineHeight: 1.7 }}>
      {phones.length > 0 && (
        <div>📞 {phones.map((p) => `(${p.ddd || ""}) ${p.number}${p.main ? " ★" : ""}`).join("  ·  ")}</div>
      )}
      {emails.length > 0 && <div>✉ {emails.join("  ·  ")}</div>}
      {(city || state) && <div>📍 {[city, state].filter(Boolean).join(" / ")}</div>}
      {contacts.length > 0 && (
        <div>👤 {contacts.map((c) => `${c.name || ""}${c.number ? ` (${c.number})` : ""}`).join("  ·  ")}</div>
      )}
    </div>
  );
};

const SuppliersTable = ({ list, selected, onSelect }) => (
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th style={th}>Fornecedor</th>
        <th style={thR}>Pedidos</th>
        <th style={thR}>Valor total</th>
        <th style={thR}>Obras</th>
      </tr>
    </thead>
    <tbody>
      {list.map((s) => (
        <tr
          key={s.supplierId}
          onClick={() => onSelect(s)}
          style={{ cursor: "pointer", background: selected?.supplierId === s.supplierId ? "#eef4ff" : "transparent" }}
        >
          <td style={td}>
            {supplierLabel(s)}
            {!s.name && <span title="Sem nome no master (CNPJ não ingerido)" style={{ color: "#b0832b" }}> ⚠</span>}
            {s.cnpj && <div style={{ fontSize: 11, color: "#888" }}>{s.cnpj}</div>}
          </td>
          <td style={num}>{s.orderCount}</td>
          <td style={num}>{fmtBRL(s.totalValue)}</td>
          <td style={num}>{s.buildingCount}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const MaterialsTable = ({ list, selected, onSelect }) => (
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th style={th}>Insumo</th>
        <th style={thR}>Un</th>
        <th style={thR}>Preço médio</th>
        <th style={thR}>Pedidos</th>
      </tr>
    </thead>
    <tbody>
      {list.map((m) => (
        <tr
          key={m.material}
          onClick={() => onSelect(m)}
          style={{ cursor: "pointer", background: selected?.material === m.material ? "#eef4ff" : "transparent" }}
        >
          <td style={td}>
            {m.material}
            {m.outlier && <Outlier />}
          </td>
          <td style={num}>{m.unit || "-"}</td>
          <td style={num}>{fmtBRL(m.avgUnitPrice)}</td>
          <td style={num}>{m.orderCount}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SupplierDetail = ({ selected, detail, loading }) => {
  if (!selected) return <p>Selecione um fornecedor para ver materiais e preços.</p>;
  return (
    <>
      <h3 title={supplierLabel(selected)}>{detail?.name || supplierLabel(selected)}</h3>
      {detail?.cnpj && <p style={{ color: "#888", marginTop: 0 }}>CNPJ: {detail.cnpj}</p>}
      {detail?.contact && <ContactBlock contact={detail.contact} />}
      {loading && <p>Carregando…</p>}
      {!loading && detail && (
        <>
          <h4 className="margin-top-12">Materiais ({detail.materials?.length || 0})</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Material</th>
                <th style={thR}>Un</th>
                <th style={thR}>Médio</th>
                <th style={thR}>Mín</th>
                <th style={thR}>Máx</th>
                <th style={thR}>Pedidos</th>
              </tr>
            </thead>
            <tbody>
              {(detail.materials || []).map((m) => (
                <tr key={`${m.material}-${m.unit}`}>
                  <td style={td}>
                    {m.material}
                    {m.outlier && <Outlier />}
                  </td>
                  <td style={num}>{m.unit || "-"}</td>
                  <td style={num}>{fmtBRL(m.avgUnitPrice)}</td>
                  <td style={num}>{fmtBRL(m.minUnitPrice)}</td>
                  <td style={num}>{fmtBRL(m.maxUnitPrice)}</td>
                  <td style={num}>{m.orderCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!detail.materials?.length && <p>Sem itens de pedido com preço.</p>}

          <h4 className="margin-top-16">Pedidos ({detail.orders?.length || 0})</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Pedido</th>
                <th style={th}>Data</th>
                <th style={thR}>Valor</th>
                <th style={th}>Obra</th>
              </tr>
            </thead>
            <tbody>
              {(detail.orders || []).map((o) => (
                <tr key={o.purchaseOrderId ?? o.formattedPurchaseOrderId}>
                  <td style={td}>{o.formattedPurchaseOrderId ?? o.purchaseOrderId}</td>
                  <td style={td}>{fmtDate(o.date)}</td>
                  <td style={num}>{fmtBRL(o.value)}</td>
                  <td style={td}>{o.building || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!detail.orders?.length && <p>Sem pedidos.</p>}
        </>
      )}
      {!loading && !detail && <p>Não foi possível carregar os dados do fornecedor.</p>}
    </>
  );
};

const MaterialDetail = ({ selected, detail, loading }) => {
  if (!selected) return <p>Selecione um insumo para ver os fornecedores e comparar preços.</p>;
  const suppliers = detail?.suppliers || [];
  return (
    <>
      <h3 title={selected.material}>{detail?.material || selected.material}</h3>
      <p style={{ color: "#888", marginTop: 0 }}>Fornecedores que fornecem este insumo (preço médio crescente)</p>
      {loading && <p>Carregando…</p>}
      {!loading && detail && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Fornecedor</th>
              <th style={thR}>Un</th>
              <th style={thR}>Médio</th>
              <th style={thR}>Mín</th>
              <th style={thR}>Máx</th>
              <th style={thR}>Pedidos</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s, idx) => (
              <tr key={s.supplierId} style={{ background: idx === 0 && suppliers.length > 1 ? "#eafaf0" : "transparent" }}>
                <td style={td}>
                  {supplierLabel(s)}
                  {idx === 0 && suppliers.length > 1 && (
                    <span title="Menor preço médio" style={{ color: "#1c7c3a" }}> ★</span>
                  )}
                  {s.cnpj && <div style={{ fontSize: 11, color: "#888" }}>{s.cnpj}</div>}
                </td>
                <td style={num}>{s.unit || "-"}</td>
                <td style={num}>{fmtBRL(s.avgUnitPrice)}</td>
                <td style={num}>{fmtBRL(s.minUnitPrice)}</td>
                <td style={num}>{fmtBRL(s.maxUnitPrice)}</td>
                <td style={num}>{s.orderCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && detail && !suppliers.length && <p>Nenhum fornecedor com preço para este insumo.</p>}
      {!loading && !detail && <p>Não foi possível carregar os dados do insumo.</p>}
    </>
  );
};

const CrossClient = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await fetchCrossClientSuppliers();
      setList(r?.suppliers || []);
      setLoading(false);
    })();
  }, []);

  const onSelect = async (s) => {
    setSelected(s);
    setDetail(null);
    setLoadingDetail(true);
    const r = await fetchCrossClientSupplier(s.normalizedDoc || s.cnpj);
    setDetail(r);
    setLoadingDetail(false);
  };

  const term = search.trim().toLowerCase();
  const filtered = term
    ? list.filter((s) => (s.name || "").toLowerCase().includes(term) || (s.cnpj || "").includes(term))
    : list;

  return (
    <>
      <p className="margin-bottom-16">
        Fornecedores (por CNPJ) que atendem 2+ clientes — compare o preço entre eles.
      </p>
      <input
        type="text"
        placeholder="Buscar fornecedor"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="margin-bottom-16"
      />
      {loading && <p>Carregando…</p>}
      {!loading && (
        <div className="flex gap-16" style={{ alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 42%", minWidth: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Fornecedor</th>
                  <th style={thR}>Clientes</th>
                  <th style={thR}>Pedidos</th>
                  <th style={thR}>Valor total</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr
                    key={s.normalizedDoc}
                    onClick={() => onSelect(s)}
                    style={{ cursor: "pointer", background: selected?.normalizedDoc === s.normalizedDoc ? "#eef4ff" : "transparent" }}
                  >
                    <td style={td}>
                      {s.name || "—"}
                      {s.cnpj && <div style={{ fontSize: 11, color: "#888" }}>{s.cnpj}</div>}
                    </td>
                    <td style={num}>{s.clientCount}</td>
                    <td style={num}>{s.totalOrders}</td>
                    <td style={num}>{fmtBRL(s.totalValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && <p>Nenhum fornecedor atende 2+ clientes (com CNPJ ingerido).</p>}
          </div>

          <div style={{ flex: "1 1 58%", minWidth: 0 }}>
            {!selected && <p>Selecione um fornecedor para comparar preços entre clientes.</p>}
            {selected && (
              <>
                <h3 title={selected.name}>{detail?.name || selected.name}</h3>
                {(detail?.cnpj || selected.cnpj) && (
                  <p style={{ color: "#888", marginTop: 0 }}>CNPJ: {detail?.cnpj || selected.cnpj}</p>
                )}
                {loadingDetail && <p>Carregando…</p>}
                {!loadingDetail &&
                  detail &&
                  (detail.clients || []).map((cl) => (
                    <div key={cl.company_id} className="margin-top-12">
                      <h4>
                        {cl.companyName}{" "}
                        <span style={{ color: "#888", fontWeight: 400 }}>({cl.materials.length} materiais)</span>
                      </h4>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={th}>Material</th>
                            <th style={thR}>Un</th>
                            <th style={thR}>Médio</th>
                            <th style={thR}>Mín</th>
                            <th style={thR}>Máx</th>
                            <th style={thR}>Ped.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cl.materials.slice(0, 60).map((m) => (
                            <tr key={m.material}>
                              <td style={td}>{m.material}</td>
                              <td style={num}>{m.unit || "-"}</td>
                              <td style={num}>{fmtBRL(m.avgUnitPrice)}</td>
                              <td style={num}>{fmtBRL(m.minUnitPrice)}</td>
                              <td style={num}>{fmtBRL(m.maxUnitPrice)}</td>
                              <td style={num}>{m.orderCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                {!loadingDetail && detail && !detail.clients?.length && <p>Sem materiais com preço.</p>}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const SupplierExplorer = () => {
  const [view, setView] = useState("client");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [buildings, setBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState(""); // "" = todas
  const [mode, setMode] = useState("suppliers"); // suppliers | materials

  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState("");

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierDetail, setSupplierDetail] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialDetail, setMaterialDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await fetchExplorerCompanies();
      setCompanies(r?.companies || []);
    })();
  }, []);

  const resetSelection = () => {
    setSelectedSupplier(null);
    setSupplierDetail(null);
    setSelectedMaterial(null);
    setMaterialDetail(null);
    setSearch("");
  };

  const loadList = async (cid, bid, m) => {
    resetSelection();
    if (!cid) {
      setSuppliers([]);
      setMaterials([]);
      return;
    }
    setLoadingList(true);
    if (m === "suppliers") {
      const r = await fetchExplorerSuppliers(cid, bid);
      setSuppliers(r?.suppliers || []);
    } else {
      const r = await fetchExplorerMaterials(cid, bid);
      setMaterials(r?.materials || []);
    }
    setLoadingList(false);
  };

  const onSelectCompany = async (id) => {
    setCompanyId(id);
    setBuildingId("");
    setBuildings([]);
    if (id) {
      const b = await fetchExplorerBuildings(id);
      setBuildings(b?.buildings || []);
    }
    loadList(id, "", mode);
  };

  const onSelectBuilding = (bid) => {
    setBuildingId(bid);
    loadList(companyId, bid, mode);
  };

  const onSelectMode = (m) => {
    if (m === mode) return;
    setMode(m);
    loadList(companyId, buildingId, m);
  };

  const onSelectSupplier = async (s) => {
    setSelectedSupplier(s);
    setSupplierDetail(null);
    setLoadingDetail(true);
    const r = await fetchExplorerSupplier(companyId, s.supplierId, buildingId);
    setSupplierDetail(r);
    setLoadingDetail(false);
  };

  const onSelectMaterial = async (m) => {
    setSelectedMaterial(m);
    setMaterialDetail(null);
    setLoadingDetail(true);
    const r = await fetchExplorerMaterial(companyId, m.material, buildingId);
    setMaterialDetail(r);
    setLoadingDetail(false);
  };

  const term = search.trim().toLowerCase();
  const filteredSuppliers = term ? suppliers.filter((s) => supplierLabel(s).toLowerCase().includes(term)) : suppliers;
  const filteredMaterials = term ? materials.filter((m) => (m.material || "").toLowerCase().includes(term)) : materials;
  const withData = buildings.filter((b) => b.hasData).length;

  return (
    <div className="supplier-explorer">
      <h2>Explorador de fornecedores</h2>
      <p className="margin-bottom-16">
        Ferramenta interna — fornecedor × cliente × obra × material × preço. Lê dados de produção.
      </p>

      <div className="flex" style={{ gap: 8, marginBottom: 16 }}>
        <button onClick={() => setView("client")} style={tabStyle(view === "client")}>
          Por cliente
        </button>
        <button onClick={() => setView("cross")} style={tabStyle(view === "cross")}>
          Cross-cliente
        </button>
      </div>

      {view === "cross" && <CrossClient />}

      {view === "client" && (
        <>
      <div className="flex gap-16 align-center margin-bottom-16" style={{ flexWrap: "wrap" }}>
        <select value={companyId} onChange={(e) => onSelectCompany(e.target.value)}>
          <option value="">Selecione um cliente…</option>
          {companies.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
              {c.erp ? ` (${c.erp})` : ""}
            </option>
          ))}
        </select>

        {companyId && (
          <select value={buildingId} onChange={(e) => onSelectBuilding(e.target.value)}>
            <option value="">Todas as obras ({withData} c/ dados)</option>
            {buildings.map((b) => (
              <option key={b.buildingId} value={b.buildingId} disabled={!b.hasData}>
                {b.hasData ? "● " : "○ "}
                {b.name}
                {b.hasData ? ` (${b.orderCount})` : " — sem dados"}
              </option>
            ))}
          </select>
        )}

        {companyId && (
          <div className="flex" style={{ gap: 8 }}>
            <button onClick={() => onSelectMode("suppliers")} style={tabStyle(mode === "suppliers")}>
              Por fornecedor
            </button>
            <button onClick={() => onSelectMode("materials")} style={tabStyle(mode === "materials")}>
              Por insumo
            </button>
          </div>
        )}

        {companyId && (
          <input
            type="text"
            placeholder={mode === "suppliers" ? "Buscar fornecedor" : "Buscar insumo"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </div>

      {loadingList && <p>Carregando…</p>}

      {!loadingList && companyId && (
        <div className="flex gap-16" style={{ alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 44%", minWidth: 0 }}>
            {mode === "suppliers" ? (
              <>
                <SuppliersTable list={filteredSuppliers} selected={selectedSupplier} onSelect={onSelectSupplier} />
                {!filteredSuppliers.length && <p>Nenhum fornecedor encontrado.</p>}
              </>
            ) : (
              <>
                <MaterialsTable list={filteredMaterials} selected={selectedMaterial} onSelect={onSelectMaterial} />
                {!filteredMaterials.length && <p>Nenhum insumo encontrado.</p>}
              </>
            )}
          </div>

          <div style={{ flex: "1 1 56%", minWidth: 0 }}>
            {mode === "suppliers" ? (
              <SupplierDetail selected={selectedSupplier} detail={supplierDetail} loading={loadingDetail} />
            ) : (
              <MaterialDetail selected={selectedMaterial} detail={materialDetail} loading={loadingDetail} />
            )}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default SupplierExplorer;
